// DryRun.tsx — (tf3-35b) the admin-only toggle on the four order forms, and the stopped dry run's banner with its cancel
// (the Run Inspector and the video page). Words: lib/dry-run.ts. (B119) the rehearsal's badge, in the Run Inspector.
import { useState } from "react";
import { useAuthStore } from "@/stores/authStore.ts";
import { opsOverviewApi } from "@/api/operations.ts";
import { DRY_RUN_LABEL, DRY_RUN_NOTE, DRY_RUN_STOPPED, DRY_RUN_CANCEL, DRY_RUN_CANCEL_REASON, DRY_RUN_CANCEL_CONFIRM,
  REHEARSAL_BADGE, REHEARSAL_RENDERED, REHEARSAL_WORKING, REHEARSAL_CANCEL, REHEARSAL_CANCEL_REASON, REHEARSAL_CANCEL_CONFIRM } from "@/lib/dry-run.ts";

export const useIsAdmin = () => { const role = useAuthStore((st) => st.user?.role); return role === "admin" || role === "superadmin"; };

/** Shown to admins only; nothing for anyone else. */
export function DryRunToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  if (!useIsAdmin()) return null;
  return (
    <label style={{ display: "block", fontSize: 13, margin: "8px 0" }}>
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} style={{ marginRight: 6 }} />
      {DRY_RUN_LABEL}
      {on && <div className="tf-muted" style={{ fontSize: 12, marginTop: 2 }}>{DRY_RUN_NOTE}</div>}
    </label>
  );
}

/** A run that stopped before its first clip: the words, and (admins) its cancel — the orchestrator's, ending dry_run_cancelled. */
export function DryRunStopped({ runId, onCancelled }: { runId: string; onCancelled: () => void }) {
  const admin = useIsAdmin();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cancel = async () => {
    if (!window.confirm(DRY_RUN_CANCEL_CONFIRM)) return;
    setBusy(true); setErr(null);
    try { await opsOverviewApi.cancelRun(runId, DRY_RUN_CANCEL_REASON); onCancelled(); }
    catch (e) { setErr((e as Error).message || "The dry run couldn't be cancelled."); }   // catch-reason: shown under the button
    finally { setBusy(false); }
  };
  return (
    <div role="status" style={{ border: "1px solid #c7d2fe", background: "#eef2ff", borderRadius: 8, padding: "10px 12px", margin: "10px 0", fontSize: 13 }}>
      <strong>{DRY_RUN_STOPPED}</strong>
      <span className="tf-muted"> — everything before the first clip is made; no clip was.</span>
      {admin && <div style={{ marginTop: 8 }}><button type="button" disabled={busy} onClick={cancel}>{DRY_RUN_CANCEL}</button></div>}
      {err && <div style={{ color: "#b91c1c", marginTop: 6 }}>{err}</div>}
    </div>
  );
}

/** (B119) a rehearsal (stand-in clips): the badge, what it is, and (admins, while it runs) its cancel. Never a deliverable. */
export function RehearsalBadge({ run, onCancelled }: { run: { id: string; status: string; rehearsalRenderedAt?: string | null }; onCancelled: () => void }) {
  const admin = useIsAdmin();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cancel = async () => {
    if (!window.confirm(REHEARSAL_CANCEL_CONFIRM)) return;
    setBusy(true); setErr(null);
    try { await opsOverviewApi.cancelRun(run.id, REHEARSAL_CANCEL_REASON); onCancelled(); }
    catch (e) { setErr((e as Error).message || "The rehearsal couldn't be cancelled."); }   // catch-reason: shown under the button
    finally { setBusy(false); }
  };
  const live = run.status === "running" || run.status === "pending";
  return (
    <div role="status" style={{ border: "1px solid #fcd34d", background: "#fffbeb", borderRadius: 8, padding: "10px 12px", margin: "10px 0", fontSize: 13 }}>
      <strong>{REHEARSAL_BADGE}</strong>
      <span className="tf-muted"> — {run.rehearsalRenderedAt ? REHEARSAL_RENDERED : REHEARSAL_WORKING}</span>
      {admin && live && <div style={{ marginTop: 8 }}><button type="button" disabled={busy} onClick={cancel}>{REHEARSAL_CANCEL}</button></div>}
      {err && <div style={{ color: "#b91c1c", marginTop: 6 }}>{err}</div>}
    </div>
  );
}
